import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';

import { OrderService } from 'src/app/services/marketplace/order.service';
import { ListingService } from 'src/app/services/marketplace/listing.service';
import { ToastService } from 'src/app/services/toast.service';
import { Order, OrderLine } from 'src/app/services/marketplace/marketplace.models';

/** A download button on an order line: a specific file, or (legacy) a format. */
interface LineDownload {
  key: string;
  label: string;
  filename: string;
  fileId?: number;
  format?: string;
}

/** One owned item in the Library tab (an order line plus its order). */
interface LibraryItem {
  line: OrderLine;
  order: Order;
}

@Component({
  selector: 'app-marketplace-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrdersComponent implements OnInit {
  orders: Order[] = [];
  loading = true;
  loadFailed = false;
  /** The load failed because the login expired. */
  needsSignIn = false;
  search = '';
  tab: 'library' | 'orders' = 'library';
  /** Library cards whose file list is open, by order line id. */
  openFiles: Record<number, boolean> = {};
  readonly skeletons = [1, 2, 3];
  /** Built once per load, not on every change detection. */
  library: LibraryItem[] = [];
  fileCount = 0;
  totalSpent = 0;
  /** Keyed `${lineId}:${download.key}` so each file on a line tracks separately. */
  downloading: Record<string, boolean> = {};

  /** Display labels for ListingAsset formats; unknown codes fall back to the code. */
  private readonly formatLabels: Record<string, string> = {
    pdf: 'PDF',
    image: 'Image',
    foundry_vtt: 'Foundry',
    roll20: 'Roll20',
    other: 'File',
  };

  /**
   * One button per downloadable file (`listing.files`): a Content Vault item
   * can have several files of one format, e.g. three map images. Falls back
   * to one button per format for responses without `files`.
   */
  downloadsFor(line: OrderLine): LineDownload[] {
    const files = line.listing_detail?.files;
    if (files?.length) {
      return files.map((file) => ({ key: `file-${file.id}`, label: file.name, fileId: file.id, filename: file.name }));
    }
    return (line.listing_detail?.available_formats ?? []).map((code) => ({
      key: code,
      label: this.formatLabels[code] ?? code,
      format: code,
      filename: `${line.listing_detail?.slug || 'download'}-${code}`,
    }));
  }

  constructor(
    private orderService: OrderService,
    private listingService: ListingService,
    private toaster: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.cdr.detectChanges();
    this.orderService.list().subscribe({
      next: (orders) => {
        this.orders = orders;
        this.buildLibrary();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        // Not "no purchases": say it failed and offer a retry (or sign-in).
        this.loading = false;
        this.loadFailed = true;
        this.needsSignIn = err?.status === 401;
        this.cdr.detectChanges();
      },
    });
  }

  /** Newest first; the Library shows paid items only (they're the ones you can download). */
  private buildLibrary(): void {
    const sorted = [...this.orders].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    this.orders = sorted;
    this.library = sorted
      .filter((order) => this.isDownloadable(order))
      .flatMap((order) => order.lines.map((line) => ({ line, order })));
    this.fileCount = this.library.reduce((sum, item) => sum + this.downloadsFor(item.line).length, 0);
    this.totalSpent = sorted
      .filter((order) => this.isDownloadable(order))
      .reduce((sum, order) => sum + Number(order.total || 0), 0);
  }

  private matches(line: OrderLine, order: Order, term: string): boolean {
    return order.order_number.toLowerCase().includes(term) ||
      (line.listing_detail?.title || '').toLowerCase().includes(term) ||
      (line.vendor_detail?.shop_name || '').toLowerCase().includes(term);
  }

  /** Library items matching the search box: item title, shop or order number. */
  get filteredLibrary(): LibraryItem[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.library;
    return this.library.filter((item) => this.matches(item.line, item.order, term));
  }

  get filteredOrders(): Order[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.orders;
    return this.orders.filter((order) => order.lines.some((line) => this.matches(line, order, term)));
  }

  get shownCount(): number {
    return this.tab === 'library' ? this.filteredLibrary.length : this.filteredOrders.length;
  }

  toggleFiles(line: OrderLine): void {
    this.openFiles[line.id] = !this.openFiles[line.id];
  }

  statusLabel(status: string): string {
    return ({
      paid: 'Paid',
      pending: 'Awaiting payment',
      failed: 'Payment failed',
      refunded: 'Refunded',
      partially_refunded: 'Partly refunded',
    } as Record<string, string>)[status] ?? status;
  }

  money(amount: string | number | null | undefined): string {
    const value = Number(amount);
    return value ? `$${value.toFixed(2)}` : 'Free';
  }

  /** Picks the file icon on a download button. */
  fileKind(item: LineDownload): 'image' | 'doc' | 'other' {
    const name = (item.filename || item.format || '').toLowerCase();
    if (item.format === 'image' || /\.(png|jpe?g|webp|gif|svg)$/.test(name)) return 'image';
    if (item.format === 'pdf' || /\.(pdf|docx?|txt|md|rtf)$/.test(name)) return 'doc';
    return 'other';
  }

  /** Only paid orders may be downloaded; the API enforces this too. */
  isDownloadable(order: Order): boolean {
    return order.status === 'paid';
  }

  isDigital(line: OrderLine): boolean {
    return line.listing_detail?.fulfillment_type === 'digital';
  }

  downloadKey(line: OrderLine, item: LineDownload): string {
    return `${line.id}:${item.key}`;
  }

  download(line: OrderLine, item: LineDownload): void {
    const key = this.downloadKey(line, item);
    if (this.downloading[key]) {
      return;
    }
    this.downloading[key] = true;

    const request$ = item.fileId != null
      ? this.listingService.downloadFile(line.listing, item.fileId)
      : this.listingService.download(line.listing, item.format!);
    request$.subscribe({
      next: (blob) => {
        this.saveBlob(blob, item.filename);
        this.downloading[key] = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.downloading[key] = false;
        this.toaster.showNote(`Couldn't download ${item.label}. Please try again.`, 'warning');
        this.cdr.detectChanges();
      },
    });
  }

  private saveBlob(blob: Blob, filename: string): void {
    // PreviewInterceptor answers the download endpoint with a stubbed JSON
    // object rather than a real file, so there's nothing to hand the browser.
    if (!(blob instanceof Blob)) {
      this.toaster.showNote('Preview mode: downloads are stubbed.', 'warning');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke on the next tick — revoking synchronously can cancel the download
    // before the browser has committed it.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
